import { useState, useEffect, useCallback } from "react";
import { onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, googleProvider, db } from "@/config/firebase";
export function useAuth() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const role = await fetchUserRole(firebaseUser.uid);
                    setUser({
                        uid: firebaseUser.uid,
                        email: firebaseUser.email ?? "",
                        displayName: firebaseUser.displayName ?? "User",
                        photoURL: firebaseUser.photoURL,
                        role,
                    });
                    setError(null);
                }
                catch (err) {
                    console.error("Failed to fetch user role:", err);
                    setUser({
                        uid: firebaseUser.uid,
                        email: firebaseUser.email ?? "",
                        displayName: firebaseUser.displayName ?? "User",
                        photoURL: firebaseUser.photoURL,
                        role: "member",
                    });
                }
            }
            else {
                setUser(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);
    const signIn = useCallback(async () => {
        try {
            setError(null);
            setLoading(true);
            await signInWithPopup(auth, googleProvider);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "Sign in failed";
            if (message.includes("popup-closed-by-user")) {
                setError(null);
            }
            else if (message.includes("unauthorized-domain")) {
                setError("This domain is not authorized for sign-in. Please contact an administrator.");
            }
            else {
                setError(message);
            }
        }
        finally {
            setLoading(false);
        }
    }, []);
    const signOut = useCallback(async () => {
        try {
            await firebaseSignOut(auth);
            setUser(null);
        }
        catch (err) {
            console.error("Sign out error:", err);
        }
    }, []);
    const getToken = useCallback(async () => {
        const currentUser = auth.currentUser;
        if (!currentUser)
            return null;
        return currentUser.getIdToken();
    }, []);
    return { user, loading, error, signIn, signOut, getToken };
}
async function fetchUserRole(uid) {
    try {
        const userDoc = await getDoc(doc(db, "users", uid));
        if (userDoc.exists()) {
            return userDoc.data().role ?? "member";
        }
        return "member";
    }
    catch {
        return "member";
    }
}
